// services/AuthManager.js - Phone-based authentication
import { tokenManager } from './TokenManager';
import { Preferences } from '@capacitor/preferences';
import { isCapacitor, isElectron } from '../utils/platform';
import { secureTokenStorage } from './SecureTokenStorage';
import { tokenLog, tokenWarn, tokenError, createLogger } from './Debug';

class AuthManager {
  constructor() {
    this.authStateListeners = new Set();
    this.isAuthenticated = false;
    this.initPromise = null;
    this.isLoggingOut = false;
    this.currentUser = null; // Store current user info (phone, has_pin, etc.)
  }

  // Lazy load services to avoid circular dependencies
  get userService() {
    if (!this._userService) {
      const { userService } = require('./index');
      this._userService = userService;
    }
    return this._userService;
  }

  get profileManager() {
    if (!this._profileManager) {
      const { profileManager } = require('./index');
      this._profileManager = profileManager;
    }
    return this._profileManager;
  }

  // Initialize auth state from storage
  async initialize() {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        // Check if we have a valid token
        const token = tokenManager.getToken();
        if (!token) {
          // No access token in memory: try refresh using stored refresh token (Capacitor/Electron)
          try {
            let hasRefresh = false;
            if (typeof window !== 'undefined' && (isCapacitor || isElectron)) {
              try {
                if (isCapacitor) {
                  const { value } = await Preferences.get({ key: 'refresh_token' });
                  hasRefresh = Boolean(value);
                } else if (isElectron) {
                  hasRefresh = Boolean(localStorage.getItem('refreshToken'));
                }
              } catch (_) {
                // ignore
              }
            }

            if (hasRefresh) {
              await this.userService.refreshToken();
              this.isAuthenticated = true;
              tokenLog('AuthManager.initialize(): refreshed using stored refresh token');
              return true;
            }
          } catch (error) {
            tokenError('AuthManager.initialize(): refresh during init (no access token) failed', error);
          }

          this.isAuthenticated = false;
          tokenWarn('AuthManager.initialize(): no token and no refresh found');
          return false;
        }

        // Access token exists; refresh if expiring/expired
        if (tokenManager.isExpiringSoon(0)) {
          try {
            await this.userService.refreshToken();
            this.isAuthenticated = true;
            tokenLog('AuthManager.initialize(): refreshed expiring token');
          } catch (error) {
            tokenError('AuthManager.initialize(): token refresh failed during init', error);
            // Do not force logout during initialization; let UI handle gracefully
            this.isAuthenticated = false;
            return false;
          }
        } else {
          this.isAuthenticated = true;
          tokenLog('AuthManager.initialize(): token valid, authenticated');
        }

        // Profile loading is handled by AuthContext.

        return this.isAuthenticated;
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  // Phone authentication - Start OTP flow
  async startPhoneAuth(phone, channel = 'sms') {
    try {
      const response = await this.userService.startPhoneAuth(phone, channel);
      tokenLog('AuthManager.startPhoneAuth(): OTP sent', { phone: phone.slice(0, -4) + '****' });
      return response;
    } catch (error) {
      tokenError('AuthManager.startPhoneAuth(): failed', error);
      throw error;
    }
  }

  // Verify OTP code
  async verifyPhoneCode(phone, code) {
    try {
      const response = await this.userService.verifyPhoneCode(phone, code);
      this.currentUser = response.user;
      tokenLog('AuthManager.verifyPhoneCode(): verified', { has_pin: response.user?.has_pin });
      return response;
    } catch (error) {
      tokenError('AuthManager.verifyPhoneCode(): failed', error);
      throw error;
    }
  }

  // Email login - Start flow
  async startEmailLogin(email) {
    try {
      const response = await this.userService.startEmailLogin(email);
      tokenLog('AuthManager.startEmailLogin(): email code sent');
      return response;
    } catch (error) {
      tokenError('AuthManager.startEmailLogin(): failed', error);
      throw error;
    }
  }

  // Email login - Complete
  async completeEmailLogin(email, code, pin = null, deviceInfo = null) {
    try {
      const response = await this.userService.completeEmailLogin(email, code, pin, deviceInfo);
      this.isAuthenticated = true;
      this.currentUser = response.user;
      this.notifyAuthStateChange(true);
      tokenLog('AuthManager.completeEmailLogin(): authenticated');
      return response;
    } catch (error) {
      this.isAuthenticated = false;
      tokenError('AuthManager.completeEmailLogin(): failed', error);
      throw error;
    }
  }

  // Set PIN for new user
  async setPin(phone, pin) {
    try {
      const response = await this.userService.setPin(phone, pin);
      this.currentUser = response.user;
      tokenLog('AuthManager.setPin(): PIN set');
      return response;
    } catch (error) {
      tokenError('AuthManager.setPin(): failed', error);
      throw error;
    }
  }

  // Complete login with OTP and PIN
  async completeLogin(phone, code, pin, deviceInfo = null) {
    try {
      const response = await this.userService.completeLogin(phone, code, pin, deviceInfo);
      this.isAuthenticated = true;
      this.currentUser = response.user;
      this.notifyAuthStateChange(true);
      tokenLog('AuthManager.completeLogin(): authenticated');
      return response;
    } catch (error) {
      this.isAuthenticated = false;
      tokenError('AuthManager.completeLogin(): failed', error);
      throw error;
    }
  }

  // Test login for test account (phone: 000 000 00 00)
  async testLogin(phone, code, pin, deviceInfo = null) {
    try {
      const response = await this.userService.testLogin(phone, code, pin, deviceInfo);
      this.isAuthenticated = true;
      this.currentUser = response.user;
      this.notifyAuthStateChange(true);
      tokenLog('AuthManager.testLogin(): authenticated');
      return response;
    } catch (error) {
      this.isAuthenticated = false;
      tokenError('AuthManager.testLogin(): failed', error);
      throw error;
    }
  }

  // Trusted device login (mobile)
  async loginWithTrustedDevice(deviceId, signature, pin = null, deviceInfo = null) {
    try {
      const response = await this.userService.verifyDevice(deviceId, signature, pin, deviceInfo);
      this.isAuthenticated = true;
      this.currentUser = response.user;
      this.notifyAuthStateChange(true);
      tokenLog('AuthManager.loginWithTrustedDevice(): authenticated with trusted device');
      return response;
    } catch (error) {
      this.isAuthenticated = false;
      tokenError('AuthManager.loginWithTrustedDevice(): failed', error);
      throw error;
    }
  }

  // Complete profile setup
  async completeProfile(firstName, lastName, title = null, specialty = null, deviceInfo = null) {
    try {
      const response = await this.userService.completeProfile(firstName, lastName, title, specialty, deviceInfo);
      tokenLog('AuthManager.completeProfile(): profile completed');
      return response;
    } catch (error) {
      tokenError('AuthManager.completeProfile(): failed', error);
      throw error;
    }
  }

  // Optional email setup
  async setEmail(phone, email) {
    try {
      const response = await this.userService.setEmail(phone, email);
      tokenLog('AuthManager.setEmail(): email set for verification');
      return response;
    } catch (error) {
      tokenError('AuthManager.setEmail(): failed', error);
      throw error;
    }
  }

  // Unified registration: PIN + optional email + profile in one call
  async registerUnified(phone, pin, firstName, lastName, email = null, title = null, specialty = null) {
    try {
      const response = await this.userService.registerUnified(
        phone, pin, firstName, lastName, email, title, specialty
      );
      this.isAuthenticated = true;
      this.currentUser = response.user;
      this.notifyAuthStateChange(true);
      tokenLog('AuthManager.registerUnified(): registration complete');
      return response;
    } catch (error) {
      tokenError('AuthManager.registerUnified(): failed', error);
      throw error;
    }
  }

  // PIN recovery
  async requestRecovery(email) {
    try {
      const response = await this.userService.requestRecovery(email);
      tokenLog('AuthManager.requestRecovery(): recovery email sent');
      return response;
    } catch (error) {
      tokenError('AuthManager.requestRecovery(): failed', error);
      throw error;
    }
  }

  async completeRecovery(token, newPin) {
    try {
      const response = await this.userService.completeRecovery(token, newPin);
      tokenLog('AuthManager.completeRecovery(): PIN reset');
      return response;
    } catch (error) {
      tokenError('AuthManager.completeRecovery(): failed', error);
      throw error;
    }
  }

  // Logout
  async logout(callApi = true) {
    // Prevent multiple simultaneous logouts
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    tokenLog('AuthManager.logout(): starting', { callApi });

    try {
      // Set auth false but DO NOT notify yet to avoid UI racing before tokens are cleared
      this.isAuthenticated = false;
      this.currentUser = null;

      // Clear all auth data FIRST to prevent flicker and unintended re-auth
      await this.clearAllAuthData();

      // Now notify listeners that auth is false after a clean state
      this.notifyAuthStateChange(false);
      tokenLog('AuthManager.logout(): auth state notified false');

      // Give React a moment to process the state change
      await new Promise(resolve => setTimeout(resolve, 0));

      // Call API logout if requested (after clearing local data)
      if (callApi) {
        try {
          await this.userService.logout();
        } catch (error) {
          const log = createLogger('AuthManager.logout');
          log.error('API logout failed:', error);
          // Continue even if API fails
        }
      }

      // On Electron: close secondary windows, clear storage, then navigate to auth page
      if (typeof window !== 'undefined' && typeof window.electronAPI !== 'undefined' && isElectron) {
        try {
          // Close recorder, liveRecorder, macro windows first
          if (window.electronAPI.closeSecondaryWindows) {
            await window.electronAPI.closeSecondaryWindows();
          }
          // Clear storage
          if (window.electronAPI.clearAppStorage) {
            await window.electronAPI.clearAppStorage();
          }
          // Navigate to auth page instead of quitting
          window.location.hash = '#/';
        } catch (err) {
          // Fallback navigation on error
          window.location.hash = '#/';
        }
      }

      // Default: let the router handle navigation based on auth state
    } catch (error) {
      tokenError('AuthManager.logout(): error', error);
      // Force Electron reset if possible, otherwise navigate even on error
      if (typeof window !== 'undefined' && typeof window.electronAPI !== 'undefined' && window.electronAPI.resetAppData && isElectron) {
        try { await window.electronAPI.resetAppData({ silent: true }); return; } catch (_) {}
      }
      // Do not force navigation; let UI react to auth state
    } finally {
      this.isLoggingOut = false;
      tokenLog('AuthManager.logout(): finished');
    }
  }

  // Clear all authentication data
  async clearAllAuthData() {
    // Clear managers
    this.profileManager.clear();
    await tokenManager.clear();
    try {
      await secureTokenStorage.clearSecureTokens();
    } catch (e) {
      tokenError('clearAllAuthData(): error clearing secure tokens', e);
    }

    // Clear localStorage
    const keysToPreserve = [
      'deviceId',
      'biometric_enabled',
      'device_trusted',
      'device_fingerprint',
      'device_private_key',
      'device_public_key_pem',
      'device_private_key_stored',
      'device_name',
      'device_trusted_date'
    ];
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Clear Capacitor preferences
    if (isCapacitor) {
      try {
        const keysToPreserveCapacitor = [
          'biometric_enabled',
          'device_trusted',
          'device_fingerprint',
          'device_private_key',
          'device_public_key_pem',
          'device_private_key_stored',
          'device_name',
          'device_trusted_date'
        ];

        const { keys } = await Preferences.keys();
        for (const key of keys) {
          if (!keysToPreserveCapacitor.includes(key)) {
            await Preferences.remove({ key });
          }
        }
      } catch (error) {
        tokenError('clearAllAuthData(): error clearing Capacitor preferences', error);
      }
    }

    // Clear subscription cache
    localStorage.removeItem('subscription');
    localStorage.removeItem('userProfile');
  }

  // Check authentication status
  async checkAuth() {
    const token = tokenManager.getToken();
    if (!token) {
      this.isAuthenticated = false;
      tokenWarn('checkAuth(): no token');
      return false;
    }

    // If token exists but expired
    if (tokenManager.isExpiringSoon(0)) {
      try {
        await this.userService.refreshToken();
        this.isAuthenticated = true;
        tokenLog('checkAuth(): refreshed expiring token');
        return true;
      } catch (error) {
        tokenError('checkAuth(): token refresh failed', error);
        await this.logout(false);
        return false;
      }
    }

    this.isAuthenticated = true;
    tokenLog('checkAuth(): token valid');
    return true;
  }

  // Add auth state change listener
  addAuthStateListener(callback) {
    this.authStateListeners.add(callback);
    return () => this.authStateListeners.delete(callback);
  }

  // Notify all listeners of auth state change
  notifyAuthStateChange(isAuthenticated) {
    this.authStateListeners.forEach(listener => {
      try {
        listener(isAuthenticated);
      } catch (error) {
        const log = createLogger('AuthManager.notifyAuthStateChange');
        log.error('Error in auth state listener:', error);
      }
    });
  }

  // Get current user info
  getCurrentUser() {
    return this.currentUser;
  }
}

// Create singleton instance
export const authManager = new AuthManager();